import React, { createContext, useContext, useState, useEffect } from "react";
import { getDocDataSnap } from "../utils/firebase/Firebase.utils";
import { companyId as ID } from "../utils/constants";

// Create context with default values
export const CompanyContext = createContext({
  companyType: "condo", // Default is condo
  terms: {
    type: "condo",
    unit: "unit",
    units: "units",
    asset: "unit",
    assets: "units",
    owner: "unit owner",
    owners: "unit owners",
    structure: "tower",
    location: "floor",
    identifier: "unit number",
  },
  getTerm: () => {},
  getCapitalizedTerm: () => {},
});

export const CompanyProvider = ({ children }) => {
  const [companyData, setCompanyData] = useState(null);
  const [terms, setTerms] = useState({
    type: "condo",
    unit: "unit",
    units: "units",
    asset: "unit",
    assets: "units",
    owner: "unit owner",
    owners: "unit owners",
    structure: "tower",
    location: "floor",
    identifier: "unit number",
  });

  useEffect(() => {
    const companyId = ID; // This is the fixed ID for the company document

    let unsubscribe;
    try {
      unsubscribe = getDocDataSnap("company", companyId, (data) => {
        setCompanyData(data);

        // Update terminology based on entityType
        if (data && data.entityType === "hoa") {
          setTerms({
            type: "hoa",
            unit: "resident",
            asset: "lot",
            assets: "lots",
            units: "residents",
            owner: "lot owner",
            owners: "lot owners",
            structure: "phase",
            location: "block",
            identifier: "lot number",
          });
        }
      });
    } catch (error) {
      console.error("Error subscribing to company data:", error);
    }

    return () => {
      // Check if unsubscribe is a function before calling it
      if (unsubscribe && typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, []);

  // Helper function to get term
  const getTerm = (term) => {
    return terms[term] || term;
  };

  // Helper function to get capitalized term
  const getCapitalizedTerm = (term) => {
    const termText = terms[term] || term;
    return termText.charAt(0).toUpperCase() + termText.slice(1);
  };

  return (
    <CompanyContext.Provider
      value={{
        companyType: companyData?.entityType || "condo",
        companyData,
        terms,
        getTerm,
        getCapitalizedTerm,
      }}
    >
      {children}
    </CompanyContext.Provider>
  );
};

// Custom hook for easier context consumption
export const useCompany = () => useContext(CompanyContext);
